<template>
    <div>
        <div class="mt-5 flex flex-col">
            <div
                id="title-general"
                class="flex flex-col md:flex-row justify-between items-center"
            >
                <div class="flex justify-between items-center">
                    <router-link
                        to="/app/banners"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                    <div v-if="id" class="ml-3">
                        <h1 class="text-3xl text-theme-base">{{ name }}</h1>
                        <p class="text-sm text-theme-muted mt-1">#{{ id }}</p>
                    </div>
                    <h1 v-else class="text-3xl ml-3">Nuevo banner</h1>
                </div>

                <custom-switch
                    v-if="$ability.hasPermission('banner:Update') && id"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
            <span class="mt-3">
                Los detalles del banner determinan donde va a ser mostrado el
                banner, que imagen debe tener y el lugar al que redirige.
            </span>
        </div>
        <div id="banner-features">
            <div class="flex flex-wrap">
                <div class="w-full mt-3 flex justify-center items-center">
                    <active-selector
                        v-model="itemComponent.active"
                        :enabled="enabledUpdate"
                        :weight="parseInt(itemComponent.sequence)"
                        require-weight
                        position-mode
                        on-label="Publicado"
                        off-label="Oculto"
                        activate-title="¿Quieres habilitar el banner?"
                        activate-hint="Elige la posición del banner en su página: el 1 aparece primero."
                        confirm-label="Aceptar"
                        @weight-change="itemComponent.sequence = $event"
                    />
                </div>
                <div
                    v-if="id"
                    class="w-full md:w-1/2 mt-3 p-0 flex justify-around"
                >
                    <span class="text-left">
                        📆💾 {{ itemComponent.createdAt | date }}
                    </span>
                    <span class="text-left">
                        📆✏ {{ itemComponent.updatedAt | date }}
                    </span>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        name="BannerName"
                        placeholder="Banner principal"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName & introValidators.name
                                ? 'Nombre de banner invalido'
                                : ''
                        "
                        @blur="introValidators.name = true"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <subline-selector
                        v-model="itemComponent.line"
                        add-root
                        label="Categoría donde se mostrará el banner"
                        :enabled="enabledUpdate"
                    />
                </div>
            </div>

            <div>
                <banner-path-manage
                    v-model="itemComponent.path"
                    :enabled="enabledUpdate"
                />
            </div>

            <div class="flex flex-wrap px-2">
                <div class="w-full mt-6">
                    <image-manage
                        v-model="itemComponent.image"
                        :enabled="enabledUpdate"
                        message="Solo puedes usar para banners imágenes responsive"
                        :default-filters="{ size: 'tripleImage' }"
                    />
                </div>
            </div>

            <div class="flex flex-wrap h-10 mt-3">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button
                        v-if="!id"
                        size="large"
                        @click="createItemComponent"
                    >
                        Crear banner
                    </app-button>
                    <app-button
                        v-else
                        size="large"
                        @click="updateItemComponent"
                    >
                        Actualizar banner
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { ROOT_LINE_NAME } from "@/common/constants";
import { nameRegex } from "@/common/lib/regex";

import BannerPathManage from "@/components/banners/BannerPathManage.vue";
import SublineSelector from "@/components/lines/SublineSelector.vue";
import ActiveSelector from "@/components/utils/ActiveSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageManage from "@/components/utils/ImageManage.vue";

export default {
    name: "BannersDetails",
    components: {
        BannerPathManage,
        SublineSelector,
        ImageManage,
        ActiveSelector,
        CustomInput
    },
    data: () => ({
        itemComponent: {
            id: 0,
            name: "",
            sequence: 1,
            active: false,
            path: { id: "", name: "", entity: "undefined", slug: "" },
            image: { id: 0 },
            line: { slug: "null", name: "" }
        },
        introValidators: { name: false },
        enabledUpdate: false,
        isLoading: false,
        loadInfo: false,
        name: "",
        id: 0
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidName() {
            return nameRegex.test(this.itemComponent.name);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("banner:Create", "/app/banners")))
                return;
            const id = Number(this.$route.params.id);
            this.id = id;
            await this.getBaseItem(id);
        } else if ("idClone" in this.$route.params) {
            if (!(await verifyPagePermission("banner:Create", "/app/banners")))
                return;
            this.id = 0;
            this.enabledUpdate = true;
            const idTmp = Number(this.$route.params.idClone);
            await this.getBaseItem(idTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.name} CLONE`,
                active: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando un banner",
                icon: "info",
                showCloseButton: true
            });
        } else {
            if (!(await verifyPagePermission("banner:Get", "/app/banners")))
                return;
            this.enabledUpdate = true;
            this.loadInfo = true;
        }
    },
    methods: {
        ...mapActions("banners", [
            "getBannerById",
            "createBanner",
            "updateBanner"
        ]),
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const banner = await this.getBannerById({ id });
                let line = banner.line;
                if (!banner.line) {
                    line = { slug: "null", name: ROOT_LINE_NAME };
                }
                this.itemComponent = { ...banner, line };
                this.name = this.itemComponent.name;
                this.loadInfo = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$loading.hide();
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/banners");
                }
            }
        },
        async updateItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateBanner(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createBanner(this.getDataSend());
                this.$loading.hide();
                this.itemComponent = { ...data, ...this.itemComponent };
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${data.id}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de banners",
                    cancelButtonText: "Nuevo banner"
                });
                if (dialogResult.isConfirmed) {
                    this.id = data.id;
                    this.$router.push(`/app/banners/detail/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/banners/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        slug: false,
                        name: false,
                        summary: false
                    };
                    this.itemComponent = {
                        name: "",
                        sequence: 1,
                        active: false,
                        path: {
                            id: "",
                            name: "",
                            entity: "undefined",
                            slug: ""
                        },
                        image: { id: 0 },
                        line: { slug: "null", name: "" }
                    };
                    this.images = [];
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        validateData() {
            this.introValidators = {
                name: true
            };

            const toComplete = [];

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (!this.itemComponent.image.id) {
                toComplete.push("Imagen");
            }

            if (!this.itemComponent.path) {
                toComplete.push("Path");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Banner invalido",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            const lineSlug = this.itemComponent.line.slug ?? "null";
            return {
                ...this.itemComponent,
                path: {
                    ...this.itemComponent.path,
                    slug:
                        this.itemComponent.path.entity === "undefined"
                            ? ""
                            : this.itemComponent.path.slug
                },
                lineSlug: lineSlug !== "null" ? lineSlug : null,
                imageId: this.itemComponent.image.id
            };
        }
    }
};
</script>
